import { supabase } from "@/integrations/supabase/client";

// Tipo temporário até que os tipos do Supabase sejam regenerados
interface AuditLog {
  id: string;
  user_id: string;
  table_name: string;
  action: string;
  record_id?: string | null;
  record_ids?: string[] | null;
  old_values?: any;
  new_values?: any;
  metadata?: Record<string, any>;
  created_at: string;
}

/**
 * Hook para registrar ações de auditoria
 * Armazena logs de ações críticas para rastreabilidade
 */
export const useAuditLog = () => {
  /**
   * Registra uma ação no log de auditoria
   */
  const logAction = async (
    tableName: string,
    action: string,
    recordId?: string,
    recordIds?: string[],
    metadata?: Record<string, any>
  ): Promise<void> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return; // Não logar se usuário não autenticado
      
      const { error } = await (supabase as any)
        .from('audit_logs')
        .insert({
          table_name: tableName,
          action,
          record_id: recordId || null,
          record_ids: recordIds || null,
          metadata: {
            ...metadata,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
          }
        });
      
      if (error) {
        // Falha silenciosa - não quebra UX
        console.warn('Audit log failed (non-blocking):', error.message);
      }
    } catch (error: any) {
      // Falha silenciosa - não quebra UX
      console.warn('Audit log error (non-blocking):', error?.message);
    }
  };
  
  /**
   * Busca logs de auditoria do usuário
   */
  const getAuditLogs = async (
    limit: number = 50,
    offset: number = 0
  ) => {
    try {
      const { data, error } = await (supabase as any)
        .from('audit_logs')
        .select('id, user_id, table_name, action, record_id, record_ids, metadata, created_at')
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);
      
      if (error) throw error;
      return data || [];
    } catch (error: any) {
      console.error('Error fetching audit logs:', error?.message);
      return [];
    }
  };
  
  /**
   * Busca logs de uma tabela específica
   */
  const getTableAuditLogs = async (
    tableName: string,
    limit: number = 20
  ) => {
    try {
      const { data, error } = await (supabase as any)
        .from('audit_logs')
        .select('id, user_id, table_name, action, record_id, record_ids, metadata, created_at')
        .eq('table_name', tableName)
        .order('created_at', { ascending: false })
        .limit(limit);
      
      if (error) throw error;
      return data || [];
    } catch (error: any) {
      console.error('Error fetching table audit logs:', error?.message);
      return [];
    }
  };
  
  /**
   * Busca logs de um registro específico
   */
  const getRecordAuditLogs = async (recordId: string) => {
    try {
      const { data, error } = await (supabase as any)
        .from('audit_logs')
        .select('id, user_id, table_name, action, record_id, record_ids, metadata, created_at')
        .eq('record_id', recordId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    } catch (error: any) {
      console.error('Error fetching record audit logs:', error?.message);
      return [];
    }
  };
  
  return { 
    logAction,
    getAuditLogs,
    getTableAuditLogs,
    getRecordAuditLogs,
  };
};
